import { useState, type FormEvent, type ReactElement } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import type { OwnerRegistration, Tenant, TenantOwner } from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { useTranslation, type CopyKey } from '@/lib/i18n';

const STATUS_KEY: Readonly<Record<TenantOwner['status'], CopyKey>> = {
  active: 'active',
  invited: 'invited',
  inactive: 'inactive',
};

// §3: a venue may be owned by more than one person, and owners are the one role
// the team screen refuses to grant — so they are added and taken off here.
export function OwnerDialog({
  tenant,
  onAdd,
  onRemove,
  onRename,
  onClose,
  error,
  busy,
}: {
  tenant: Tenant;
  onAdd: (registration: OwnerRegistration) => void;
  onRemove: (owner: TenantOwner) => void;
  onRename: (owner: TenantOwner, name: string) => void;
  onClose: () => void;
  error: string | null;
  busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  // §4 gives settings, layout and team to owners alone: a venue stripped of its
  // last one is one its own staff cannot configure. The API refuses it too.
  const only = tenant.owners.length === 1;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // §4: an owner is on no team screen, so their name is corrected right here.
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  function submit(event: FormEvent): void {
    event.preventDefault();
    onAdd({ name: name.trim(), email });
    setName('');
    setEmail('');
  }

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pt-safe pb-safe">
      <button
        type="button"
        aria-label={t('close')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-label={t('manageOwners')}
        onSubmit={submit}
        className="relative flex max-h-full w-full max-w-sm flex-col overflow-y-auto rounded-2xl bg-card p-5 shadow-xl"
      >
        <h2 className="mb-1 text-base font-bold">{t('manageOwners')}</h2>
        <p className="mb-4 text-xs text-muted-foreground">{tenant.name}</p>

        {tenant.owners.length === 0 ? (
          <p className="mb-4 rounded-lg border px-3 py-2 text-xs text-muted-foreground">
            {t('ownerMissing')}
          </p>
        ) : (
          <ul className="mb-4 flex flex-col gap-1.5">
            {tenant.owners.map((owner) => (
              <li key={owner.id} className="flex items-center gap-2 rounded-lg border px-3 py-2">
                <span className="min-w-0 flex-1">
                  {editing?.id === owner.id ? (
                    <input
                      aria-label={`${t('renameOwner')} — ${owner.email}`}
                      value={editing.name}
                      onChange={(event) => setEditing({ id: owner.id, name: event.target.value })}
                      className="mb-1 w-full rounded-lg border px-2 py-1 text-base"
                    />
                  ) : (
                    <b className="block truncate text-xs font-semibold">
                      {owner.name ?? owner.email}
                    </b>
                  )}
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {owner.email} · {t(STATUS_KEY[owner.status])}
                  </span>
                </span>
                {editing?.id === owner.id ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy || editing.name.trim() === ''}
                    aria-label={`${t('save')} — ${owner.email}`}
                    onClick={() => {
                      onRename(owner, editing.name.trim());
                      setEditing(null);
                    }}
                  >
                    <Check className="size-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    aria-label={`${t('renameOwner')} — ${owner.email}`}
                    onClick={() => setEditing({ id: owner.id, name: owner.name ?? '' })}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || only}
                  title={only ? t('ownerLastOne') : undefined}
                  aria-label={`${t('remove')} — ${owner.email}`}
                  onClick={() => onRemove(owner)}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* §4: an accepted address is a sign-in identity and never moves. */}
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
          {only && `${t('ownerLastOne')} `}
          {t('ownerAddressNote')}
        </p>

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="owner-name"
        >
          {t('name')}
        </label>
        <input
          id="owner-name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mb-3 w-full rounded-lg border px-3 py-2 text-base"
        />

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="owner-email"
        >
          {t('email')}
        </label>
        <input
          id="owner-email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mb-1 w-full rounded-lg border px-3 py-2 text-base"
        />

        {error !== null && (
          <p role="alert" className="mt-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('close')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('addOwner')}
          </Button>
        </div>
      </form>
    </div>
  );
}
